import {Link, Text} from '@primer/react-brand/esm'
import {ArrowUpRightIcon, ChevronRightIcon, type Icon} from '@primer/octicons-react'
import {clsx} from 'clsx'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {useUrlContext} from '../../contexts/UrlContext'

import styles from './NavLink.module.css'
import redesignStyles from '../../MarketingNavigationRedesign.module.css'

export type NavLinkType = {
  className?: string
  context?: string
  external?: boolean
  icon?: Icon
  image?: {light: string; dark: string}
  label?: string
  render?: boolean
  subtitle?: string
  title: string
  url: string
  withArrow?: boolean
}

export function NavLink(props: NavLinkType & {redesign?: boolean}) {
  const {shouldUseDotcomLinks} = useUrlContext()

  const {
    className,
    context,
    external = false,
    icon: IconComponent,
    label,
    render = true,
    subtitle,
    title,
    url,
    withArrow = false,
    image,
    redesign = false,
  } = props

  if (render === false) {
    return null
  }

  const resolvedUrl = shouldUseDotcomLinks && url.startsWith('/') ? `https://github.com${url}` : url

  const analyticsAttrs = {
    ...getAnalyticsEvent({
      action: title,
      tag: 'link',
      context: context || '',
      location: 'navbar',
    }),
  }

  if (redesign) {
    return (
      <a
        href={resolvedUrl}
        {...analyticsAttrs}
        target={external ? '_blank' : undefined}
        rel={external ? 'noreferrer' : undefined}
        className={clsx(redesignStyles.Link, image && redesignStyles.Card)}
      >
        {image ? (
          <span className={redesignStyles.Thumbnail} aria-hidden="true">
            <img src={image.light} alt="" width={240} height={150} className={redesignStyles.LightImage} />
            <img src={image.dark} alt="" width={240} height={150} className={redesignStyles.DarkImage} />
          </span>
        ) : null}
        {IconComponent ? (
          <span className={redesignStyles.Icon}>
            <IconComponent size={16} />
          </span>
        ) : null}
        <span className={redesignStyles.Copy}>
          <span className={redesignStyles.LinkTitle}>
            <span className={redesignStyles.LinkText}>{title}</span>
            {external ? <ArrowUpRightIcon size={12} aria-label="(opens in a new tab)" /> : null}
            {withArrow ? <ChevronRightIcon size={16} className={redesignStyles.Arrow} /> : null}
          </span>
          {subtitle ? <span className={redesignStyles.Subtitle}>{subtitle}</span> : null}
        </span>
      </a>
    )
  }

  if (external) {
    return (
      <Link href={resolvedUrl} {...analyticsAttrs} arrowDirection="none" isExternal target="_blank" rel="noreferrer">
        {title}
      </Link>
    )
  }

  if (withArrow) {
    return (
      <Link href={resolvedUrl} {...analyticsAttrs} arrowDirection="end">
        {title}
      </Link>
    )
  }

  if (subtitle || IconComponent || label) {
    return (
      <Link href={resolvedUrl} {...analyticsAttrs} arrowDirection="none" className={styles.link}>
        <Text as="span" className={styles.title} size="200" weight="medium">
          {IconComponent ? <IconComponent size={16} className={styles.icon} /> : null}

          {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
          {title}

          {label ? <sup className={styles.label}>{label}</sup> : null}
        </Text>

        {subtitle ? (
          <Text as="span" size="200" variant="muted" className={styles.subtitle}>
            {subtitle}
          </Text>
        ) : null}
      </Link>
    )
  }

  if (className) {
    return (
      <a href={resolvedUrl} {...analyticsAttrs} className={className}>
        {title}
      </a>
    )
  }

  return (
    <Link href={resolvedUrl} {...analyticsAttrs} arrowDirection="none">
      {title}
    </Link>
  )
}
